import { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { SiteLayout } from "@/components/SiteLayout";
import { Loading } from "@/components/States";
import { NotFoundView } from "@/components/NotFoundView";
import { AnalyticsLoader, RouteAnalytics } from "@/lib/seo";
import { ConsentBanner } from "@/components/ConsentBanner";

import HomePage from "@/pages/HomePage";
import GameDayPage from "@/pages/GameDayPage";
import LeaguePage from "@/pages/LeaguePage";
import TeamPage from "@/pages/TeamPage";
import VenueGuidePage from "@/pages/VenueGuidePage";
import VenueHotelsPage from "@/pages/VenueHotelsPage";
import EventPage from "@/pages/EventPage";
import CityPage from "@/pages/CityPage";
import GuidePage from "@/pages/GuidePage";
import { VenuesHubPage, DestinationsHubPage, GuidesHubPage } from "@/pages/HubPages";
import OutboundPage from "@/pages/OutboundPage";
import { PrivacyPage, TermsPage, AffiliateDisclosurePage, AboutPage, ContactPage } from "@/pages/LegalPages";

// Code-split the admin bundle (incl. recharts) so public pages stay lean.
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminContent = lazy(() => import("@/pages/admin/AdminContent"));
const AdminClicks = lazy(() => import("@/pages/admin/AdminClicks"));
const AdminAutomation = lazy(() => import("@/pages/admin/AdminAutomation"));
const AdminCommercial = lazy(() => import("@/pages/admin/AdminCommercial"));
const AdminAssetSummary = lazy(() => import("@/pages/admin/AdminAssetSummary"));
const AdminDistribution = lazy(() => import("@/pages/admin/AdminDistribution"));
const AdminTargets = lazy(() => import("@/pages/admin/AdminTargets"));
const AdminAutopilot = lazy(() => import("@/pages/admin/AdminAutopilot"));
const AdminBookings = lazy(() => import("@/pages/admin/AdminBookings"));

function NotFoundPage() {
  return <NotFoundView />;
}

// Destinations consolidated into the canonical cities model — redirect detail pages.
function DestinationRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/cities/${slug}`} replace />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AnalyticsLoader />
        <RouteAnalytics />
        <ConsentBanner />
        <Toaster position="top-center" richColors />
        <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="commercial" element={<AdminCommercial />} />
            <Route path="asset-summary" element={<AdminAssetSummary />} />
            <Route path="automation" element={<AdminAutomation />} />
            <Route path="clicks" element={<AdminClicks />} />
            <Route path="distribution" element={<AdminDistribution />} />
            <Route path="targets" element={<AdminTargets />} />
            <Route path="autopilot" element={<AdminAutopilot />} />
            <Route path="bookings" element={<AdminBookings />} />
            <Route path="content/:collection" element={<AdminContent />} />
          </Route>

          <Route element={<SiteLayout />}>
            <Route path="/" element={<HomePage />} />
            <Route path="/gameday" element={<GameDayPage />} />
            <Route path="/outbound" element={<OutboundPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
            <Route path="/events/:slug" element={<EventPage />} />
            <Route path="/venues" element={<VenuesHubPage />} />
            <Route path="/venues/:slug" element={<VenueGuidePage />} />
            <Route path="/venues/:slug/hotels" element={<VenueHotelsPage />} />
            <Route path="/cities/:slug" element={<CityPage />} />
            <Route path="/destinations" element={<DestinationsHubPage />} />
            <Route path="/destinations/:slug" element={<DestinationRedirect />} />
            <Route path="/guides" element={<GuidesHubPage />} />
            <Route path="/guides/:slug" element={<GuidePage />} />

            {/* GameDay hierarchy — dynamic, ranked below static routes */}
            <Route path="/:league" element={<LeaguePage />} />
            <Route path="/:league/:team" element={<TeamPage />} />
            <Route path="/:league/:team/:venue" element={<VenueGuidePage />} />
            <Route path="/:league/:team/:venue/hotels" element={<VenueHotelsPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
